import { useState } from "react";

import type { MultipleChoiceConfig, OptionId } from "../../blockTypes";
import ChoiceQuestion from "./ChoiceQuestion";

interface MultipleChoiceBlockProps {
    config?: MultipleChoiceConfig;
    onComplete?: () => void;
    onAnswer?: (isCorrect: boolean) => void;
}

// Correct only when the selection contains exactly the right options: no missing, no extra
const isSameSelection = (selected: OptionId[], correct: OptionId[]): boolean =>
    selected.length === correct.length && selected.every((id) => correct.includes(id));

const MultipleChoiceBlock = ({ config, onComplete, onAnswer }: MultipleChoiceBlockProps) => {
    const { prompt, options = [], correctOptionIds = [], explanation } = config ?? {};

    const [selectedIds, setSelectedIds] = useState<OptionId[]>([]);
    const [isChecked, setIsChecked] = useState(false);

    const isCorrect = isChecked && isSameSelection(selectedIds, correctOptionIds);

    // Clicking an option adds it, clicking it again removes it
    const handleSelect = (id: OptionId) => {
        if (isChecked) return;

        setSelectedIds((current) =>
            current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
        );
    };

    const handleCheck = () => {
        if (selectedIds.length === 0 || isChecked) return;

        const correct = isSameSelection(selectedIds, correctOptionIds);
        setIsChecked(true);
        onAnswer?.(correct);
        if (correct) onComplete?.();
    };

    return (
        <ChoiceQuestion
            prompt={prompt}
            explanation={explanation}
            options={options}
            selectedIds={selectedIds}
            correctIds={correctOptionIds}
            isChecked={isChecked}
            isCorrect={isCorrect}
            onSelect={handleSelect}
            onCheck={handleCheck}
        />
    );
};

export default MultipleChoiceBlock;